import React, { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import './AddEditUser.css';
import { handleGetRequest } from '../../services/GetTemplate';
import { handlePostRequest } from '../../services/PostTemplate';
import { useDispatch } from 'react-redux';
import { handlePutRequest } from '../../services/PutTemplate';
import Loader from '../../components/loader';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';



function AddEditUser({ onHideSidebar, isEditing, editData, editAble, fetchUsersData }) {
    const [roleIDOptions, setroleIDOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([
        { id: 1, label: 'Active', value: 'Active' },
        { id: 2, label: 'Inactive', value: 'Inactive' }
    ]);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();

    const userSchema = Yup.object().shape({
        name: Yup.string().required('Name is required'),
        roleID: Yup.string().required('Role is required'),
        email: Yup.string().email('Invalid email').required('Email is required'),
        phone: Yup.string()
            .matches(/^[0-9]{11}$/, 'Phone number must be 11 digits')
            .required('Phone number is required'),
        status: Yup.string().required('Status is required'),
        password: Yup.string()
            .test('password-required', 'Password is required', function (value) {
                if (isEditing && !value) {
                    return true;
                }
                return value ? value.length >= 6 : false;
            })
            .min(6, 'Password must be at least 6 characters')
    });

    const formik = useFormik({
        initialValues: {
            name: editData?.name || '',
            roleID: editData?.roleID || '',
            email: editData?.email || '',
            phone: editData?.mobileNumber || '',
            status: editData?.isActive ? 'Active' : editData?.isActive === false ? 'Inactive' : 'Active',
            password: '',
            photo: null
        },
        validationSchema: userSchema,
        onSubmit: async (values) => {
            if (isEditing) {
                try {
                    setLoading(true);

                    const obj = {
                        id: editData?.id,
                        name: values.name,
                        email: values.email,
                        roleID: values.roleID,
                        mobileNumber: values.phone,
                        password: values.password,
                        isActive: values.status === 'Active' ? true : false
                    };

                    const response = await dispatch(handlePutRequest(obj, '/api/user'));
                    if (response?.success) {
                        fetchUsersData();
                        onHideSidebar();
                    }
                } catch (error) {
                    console.error('Error:', error);
                } finally {
                    setLoading(false);
                }
            } else {
                try {
                    setLoading(true);

                    const obj = {
                        name: values.name,
                        email: values.email,
                        roleID: values.roleID,
                        mobileNumber: values.phone,
                        password: values.password
                    };

                    const response = await dispatch(handlePostRequest(obj, '/api/user',false ,false));
                    if (response?.success) {
                        fetchUsersData();
                        onHideSidebar();
                        toast.success(response?.message || 'User added successfully');
                    }
                    else {
                        toast.error(response?.message || 'Failed to add user')
                    }
                } catch (error) {
                    console.error('Error:', error);
                } finally {
                    setLoading(false);
                }
            }
        }
    });

    const handleroleIDChange = (e) => {
        formik.setFieldValue('roleID', e.value);
    };

    const handleStatusChange = (e) => {
        formik.setFieldValue('status', e.value);
    };

    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            formik.setFieldValue('photo', file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const getRoleID = async () => {
        try {
            setLoading(true);
            const response = await handleGetRequest('/api/role');
            if (response?.success) {
                const formattedOptions = response.data.map((role) => ({
                    label: role.name,
                    value: role.id
                }));
                setroleIDOptions(formattedOptions);
            } else {
                console.error('Failed to fetch roles:', response?.message);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getRoleID();
    }, []);

    return (
        <>
            <div className="sidebar-header">
                <div className="header-content">
                    <h2>{isEditing ? 'Edit User' : 'Add User'}</h2>
                    <Button icon="pi pi-times" onClick={onHideSidebar} className="close-button" />
                </div>
            </div>

            <div className="p-4">
                <form onSubmit={formik.handleSubmit} className="mt-2">
                    <div className="">
                        {/* Name Field */}
                        <div className="form-group">
                            <label className="form-label">Name</label>
                            <InputText
                                id="name"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`form-control ${formik.touched.name && formik.errors.name ? 'is-invalid' : ''}`}
                                placeholder="Enter name"
                            />
                            {formik.touched.name && formik.errors.name && <div className="invalid-feedback">{formik.errors.name}</div>}
                        </div>

                        {/* roleID Field */}
                        <div className="form-group">
                            <label className="form-label">Role</label>
                            <Dropdown
                                id="roleID"
                                name="roleID"
                                options={roleIDOptions}
                                value={formik.values.roleID}
                                onChange={handleroleIDChange}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select Role"
                                className={`${formik.touched.roleID && formik.errors.roleID ? 'is-invalid' : ''}`}
                            />
                            {formik.touched.roleID && formik.errors.roleID && <div className="invalid-feedback">{formik.errors.roleID}</div>}
                        </div>

                        {/* Email Field */}
                        <div className="form-group">
                            <label className="form-label">Email</label>
                            <InputText
                                id="email"
                                name="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`form-control ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                                placeholder="Enter email"
                            />
                            {formik.touched.email && formik.errors.email && <div className="invalid-feedback">{formik.errors.email}</div>}
                        </div>

                        {/* Phone Number Field */}
                        <div className="form-group">
                            <label className="form-label">Phone Number</label>
                            <InputText
                                id="phone"
                                name="phone"
                                value={formik.values.phone}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`form-control ${formik.touched.phone && formik.errors.phone ? 'is-invalid' : ''}`}
                                placeholder="Enter phone number"
                            />
                            {formik.touched.phone && formik.errors.phone && <div className="invalid-feedback">{formik.errors.phone}</div>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">Password</label>
                            <InputText
                                id="password"
                                name="password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`form-control ${formik.touched.password && formik.errors.password ? 'is-invalid' : ''}`}
                                placeholder="Enter password"
                            />
                            {formik.touched.password && formik.errors.password && <div className="invalid-feedback">{formik.errors.password}</div>}
                        </div>

                        {/* Status Field */}
                        {/* <div className="form-group">
                            <label className="form-label">Status</label>
                            <Dropdown
                                id="status"
                                name="status"
                                options={statusOptions}
                                value={formik.values.status}
                                onChange={handleStatusChange}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select status"
                                className={`${formik.touched.status && formik.errors.status ? 'is-invalid' : ''}`}
                            />
                            {formik.touched.status && formik.errors.status && <div className="invalid-feedback">{formik.errors.status}</div>}
                        </div> */}

                        {/* Submit Button */}
                        <div className="mt-4">
                            <Button type="submit" loading={loading} label={isEditing ? 'Update User' : 'Add User'} className="submit-button" />
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
}

export default AddEditUser;
